// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import { inheritAriaAttributes, pickFocusEventAttributes } from '../../helpers'
import type { Value } from '../controls'
import { CUSTOM_ERROR_FLAGS, NO_ERROR_FLAGS, VALID_STATE } from '../controls'
import type { Size } from '../shared'

/**
 * @see https://quartz.se.com/build/components/switch
 */
@Component({
  tag: 'qds-switch',
  formAssociated: true,
  shadow: { delegatesFocus: true },
  styleUrl: 'switch.css',
})
export class Switch implements ComponentInterface {
  /**
   * Adds vertical margin to the switch for alignment.
   *
   * This is useful when creating inline layouts so that the first lines have
   * the correct vertical centering.
   */
  @Prop() public readonly inline: boolean = false

  /**
   * The switches's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   *Sets the switch's state.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public checked?: boolean

  /**
   * Prevents the switch from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop() public readonly disabled?: boolean

  /**
   * The [`<form>`](https://developer.mozilla.org/docs/Web/HTML/Element/form)
   * element to associate the switch with (its form owner).
   *
   * The value of this property must be the id of a `<form>` in the same
   * document. If this property is not set, the `<qds-switch>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This property lets you associate `<qds-switch>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   *an ancestor `<form>` element.
   *
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly form: ElementInternals['form'] | string = null

  /**
   * Returns a list of the [`<label>`][] elements associated with the
   * `qds-switch` element.
   *
   * [`<label>`]: https://developer.mozilla.org/docs/Web/HTML/Element/label
   *
   * @readonly
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly labels: HTMLInputElement['labels'] = null

  /**
   * The name of the switch, which is submitted with the form data.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * The value of the switch, submitted as a name/value pair with form data.
   *
   * @webnative
   */
  @Prop() public readonly value?: Value

  /**
   * The error message that would be shown to the user if the `<qds-switch>`
   * was to be checked for validity.
   *
   * @readonly
   * @webnative
   */
  @Prop()
  public readonly validationMessage: ElementInternals['validationMessage'] = ''

  /**
   * The [`ValidityState`][] object for this `<qds-switch>`.
   *
   * [`ValidityState`]: https://developer.mozilla.org/docs/Web/API/ValidityState
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly validity: ElementInternals['validity'] = VALID_STATE

  /**
   * True if `<qds-switch>` will be validated when the form is submitted;
   * false otherwise.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly willValidate: ElementInternals['willValidate'] = false

  @AttachInternals() private readonly internals!: ElementInternals

  /**
   * Emitted when the switch loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when a change to the switch's state is committed by the user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the switch gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  @State() private inheritedAttributes: Attributes = {}

  get #computedChecked(): boolean {
    return this.checked ?? false
  }

  get #computedDisabled(): boolean {
    return (
      (this.host.matches(':disabled') || (this.disabled ?? false)) &&
      this.host.getAttribute('disabled') !== 'false'
    )
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedValue(): string | null {
    // eslint-disable-next-line unicorn/no-null
    return this.value == null ? null : this.value.toString()
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    event.preventDefault()

    this.checked = !this.#computedChecked
    this.changeEmitter.emit()
  }

  @Watch('checked')
  protected checkedChanged(): void {
    this.#updateFormValue()
    this.internals.ariaChecked = this.#computedChecked.toString()
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.#updateFormValue()
    this.internals.ariaDisabled = this.#computedDisabled.toString()
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('value')
  protected valueChanged(): void {
    this.#updateFormValue()
  }

  public componentWillLoad(): void {
    this.internals.role = 'switch'
    this.checkedChanged()
    this.disabledChanged()

    this.#defineGetter('form', () => this.internals.form)
    this.#defineGetter('labels', () => this.internals.labels)
    this.#defineGetter(
      'validationMessage',
      () => this.internals.validationMessage,
    )
    this.#defineGetter('validity', () => this.internals.validity)
    this.#defineGetter('willValidate', () => this.internals.willValidate)

    this.inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public render() {
    return (
      <div
        class={{
          'qds-container': true,
          'qds-disabled': this.#computedDisabled,
          'qds-inline': this.inline,
        }}
        data-size={this.#computedSize}
      >
        <input
          aria-hidden="true"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={this.host.autofocus}
          checked={this.checked}
          class="qds-switch"
          disabled={this.disabled}
          onBlur={this.#onBlur}
          onFocus={this.#onFocus}
          tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
          type="checkbox"
          value={this.value ?? undefined}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.inheritedAttributes}
        />
        <div class="qds-track" data-size={this.#computedSize}>
          <div class="qds-knob" data-size={this.#computedSize} />
        </div>
      </div>
    )
  }

  public checkValidity: ElementInternals['checkValidity'] = () =>
    this.internals.checkValidity()

  public reportValidity: ElementInternals['reportValidity'] = () =>
    this.internals.reportValidity()

  public setCustomValidity: HTMLInputElement['setCustomValidity'] = (error) => {
    if (error) this.internals.setValidity(CUSTOM_ERROR_FLAGS, error, this.host)
    else this.internals.setValidity(NO_ERROR_FLAGS)
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }

  #updateFormValue(): void {
    this.internals.setFormValue(
      this.#computedChecked && !this.#computedDisabled
        ? this.#computedValue
        : // eslint-disable-next-line unicorn/no-null
          null,
    )
  }
}
